import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription } from "@provenleap/ui";
import { Check, X, ArrowRight, Leaf, Plant, Tree, TreeEvergreen } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import {
  OVERAGE_RATES,
  PLAN_DISPLAY_NAMES,
  PLAN_LIMITS,
  PLAN_PRICING,
  formatFeatureValue,
} from "@provenleap/types";
import type { Plan, PlanLimits, PlanFeature } from "@provenleap/types";
import { SectionArrow } from "./SectionArrow";
import { TOTAL_INTEGRATIONS } from "../../lib/integrations";

type BillingInterval = "monthly" | "annual";

/** Features that should show as disabled (grey X) rather than hidden when false. */
const DISABLED_FEATURES: Set<keyof PlanLimits> = new Set([
  "prioritySupport",
  "restApiAccess",
  "customDomain",
  "goalsAlertsTasks",
  "pdfBranding",
]);

/** Features that still show their value (e.g. "ProvenLeap") when disabled. */
const SHOW_VALUE_WHEN_DISABLED: Set<keyof PlanLimits> = new Set();

interface FeatureRow {
  key: keyof PlanLimits;
  label: string;
}

const FEATURE_ROWS: FeatureRow[] = [
  { key: "clientLimit", label: "Clients included" },
  { key: "teamMembers", label: "Seats" },
  { key: "pdfBranding", label: "Branding" },
  { key: "goalsAlertsTasks", label: "Goals, Alerts & Tasks" },
  { key: "customDomain", label: "Custom Domain" },
  { key: "restApiAccess", label: "REST API Access" },
  { key: "prioritySupport", label: "Priority Support" },
];

function formatPricingValue(key: PlanFeature, value: PlanLimits[PlanFeature]): string {
  return formatFeatureValue(key, value, "—");
}

const PRICING = PLAN_PRICING;

function formatUsd(amount: number): string {
  const cents = Math.round(amount * 100);
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

const planConfigs: { plan: Plan; description: string; icon: Icon }[] = [
  { plan: "free", description: "For freelancers", icon: Leaf },
  { plan: "starter", description: "For growing small teams", icon: Plant },
  { plan: "pro", description: "For established agencies", icon: Tree },
  { plan: "agency", description: "For large agencies", icon: TreeEvergreen },
];

export function PricingSection() {
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  return (
    <div id="pricing" className="pt-8 pb-12 sm:pt-12 sm:pb-14 scroll-mt-20">
      {/* px-6 on mobile keeps the plan cards off the screen edges (roomier than
          the tighter px-3 the text sections use); desktop unchanged at sm:px-8. */}
      <div className="max-w-[1240px] mx-auto px-6 sm:px-8">
        <div className="text-center mb-12">
          <SectionArrow />
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            Simple, transparent pricing
          </h2>
          <p className="mt-4 text-lg text-foreground/85 max-w-[560px] mx-auto">
            Start free, upgrade as you grow. Paid plans include client capacity,
            with published rates for additional active clients.
          </p>
        </div>

        {/* Monthly / Annual toggle. ml-0 on mobile centers the group cleanly under
            justify-center; md:ml-32 restores the desktop nudge that visually re-centers
            the group under the 4-card grid against the long right-side "2 months free"
            label. flex-wrap + gap-y-2 lets the group wrap without overflow on narrow phones. */}
        <div className="flex flex-wrap items-center justify-center gap-3 gap-y-2 mb-8 ml-0 md:ml-32">
          <span
            className={`text-sm font-medium cursor-pointer ${interval === "monthly" ? "text-foreground" : "text-muted-foreground"}`}
            onClick={() => setInterval("monthly")}
          >
            Monthly
          </span>
          <label
            style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: "pointer" }}
            aria-label="Toggle billing interval"
          >
            <input
              type="checkbox"
              checked={interval === "annual"}
              onChange={(e) => setInterval(e.target.checked ? "annual" : "monthly")}
              style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 9999,
                backgroundColor: interval === "annual" ? "var(--color-primary)" : "#d1d5db",
                transition: "background-color 0.2s",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 2,
                left: interval === "annual" ? 22 : 2,
                width: 20,
                height: 20,
                borderRadius: 9999,
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </label>
          <span
            className={`text-sm font-medium cursor-pointer ${interval === "annual" ? "text-foreground" : "text-muted-foreground"}`}
            onClick={() => setInterval("annual")}
          >
            Annual <ArrowRight size={16} className="inline-block align-middle mx-1" /> <span className="text-green-600 font-semibold">2 months free</span>
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {planConfigs.map(({ plan, description, icon: PlanIcon }) => {
            const limits = PLAN_LIMITS[plan];
            const isPaid = plan !== "free";
            const pricing = isPaid ? PRICING[plan as Exclude<Plan, "free">] : null;
            const overageRates = isPaid
              ? OVERAGE_RATES[plan as Exclude<Plan, "free">]
              : null;

            return (
              <Card
                key={plan}
                className="relative flex flex-col !rounded-[32px] shadow-xs p-2"
                style={{ border: "0.5px solid var(--hairline)" }}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <PlanIcon size={24} className="text-foreground shrink-0" />
                    <CardTitle className="text-lg">{PLAN_DISPLAY_NAMES[plan]}</CardTitle>
                  </div>
                  <CardDescription className="text-[15px] pb-1.5">{description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  {/* Price */}
                  <div className="mb-4">
                    {isPaid && pricing ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-foreground">
                            ${interval === "monthly" ? pricing.monthly : pricing.effectiveMonthly}
                          </span>
                          <span className="text-sm text-muted-foreground">/mo</span>
                        </div>
                        {interval === "annual" && (
                          <p className="text-sm text-muted-foreground mt-3">
                            Billed as ${pricing.annual}/year
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-foreground">$0</span>
                        <span className="text-sm text-muted-foreground">/mo</span>
                      </div>
                    )}
                  </div>

                  {/* Feature list */}
                  <ul className="space-y-2.5 flex-1 mb-6 text-[15px]">
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 [&_path]:!fill-[#22c55e]"><Check size={18} weight="bold" /></span>
                      <span>
                        <span className="text-muted-foreground">Integrations</span>{": "}<span className="font-medium text-foreground">{TOTAL_INTEGRATIONS}</span>
                      </span>
                    </li>
                    {FEATURE_ROWS.map((row) => {
                      const value = limits[row.key];
                      const isDisabled = value === false && DISABLED_FEATURES.has(row.key);

                      if (value === false && !isDisabled) return null;

                      return (
                        <li
                          key={row.key}
                          className={`flex items-start gap-2 ${isDisabled ? "text-muted-foreground/50" : ""}`}
                        >
                          {isDisabled ? (
                            <X size={18} weight="bold" className="text-muted-foreground/40 mt-0.5 shrink-0" />
                          ) : (
                            <span className="mt-0.5 shrink-0 [&_path]:!fill-[#22c55e]"><Check size={18} weight="bold" /></span>
                          )}
                          <span>
                            <span className={isDisabled ? "text-muted-foreground/50" : "text-muted-foreground"}>{row.label}</span>
                            {!isDisabled && <>{": "}<span className="font-medium text-foreground">{formatPricingValue(row.key, value)}</span></>}
                            {isDisabled && SHOW_VALUE_WHEN_DISABLED.has(row.key) && (
                              <>{": "}<span className="font-medium text-muted-foreground/50">{formatPricingValue(row.key, value)}</span></>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  {isPaid && overageRates && (
                    <p
                      className="mb-5 text-[14px] leading-relaxed text-muted-foreground"
                      data-testid={`overage-rate-${plan}`}
                    >
                      Additional active clients: {formatUsd(overageRates.monthly)} each/month or{" "}
                      {formatUsd(overageRates.annual)} each/year.
                    </p>
                  )}

                  <Button
                    asChild
                    variant="brand"
                    className="w-full mt-auto min-h-11"
                  >
                    <Link to="/waitlist">
                      Join Waitlist
                      <ArrowRight size={16} className="ml-2" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="mt-6 mx-auto max-w-4xl text-center text-sm leading-relaxed text-muted-foreground">
          Additional-client charges apply only above the paid plan&apos;s included
          active-client count. Mid-cycle additions are prorated: monthly-plan
          adjustments normally appear on the next invoice, while annual-plan
          adjustments are invoiced immediately. Removing a client reduces future
          billing quantity without a mid-cycle credit. All amounts are USD and
          exclude applicable taxes. See our{" "}
          <Link to="/terms" className="underline hover:text-foreground">
            billing terms
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
